import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/constants';

interface Accessory {
  _id: string;
  name: string;
  type: string;
  color: string;
  description: string;
  images: string[];
  createdAt: string;
}

const AccessoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [accessory, setAccessory] = useState<Accessory | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  useEffect(() => {
    const fetchAccessory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/accessories/${id}`);
        setAccessory(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching accessory details:', error);
        setError('Aksesuar detayları yüklenirken bir hata oluştu.');
        setLoading(false);
      }
    };

    fetchAccessory();
  }, [id, apiUrl]);

  const handleDelete = async () => {
    if (window.confirm('Bu aksesuarı silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`${apiUrl}/accessories/${id}`);
        navigate('/accessories');
      } catch (error) {
        console.error('Error deleting accessory:', error);
        setError('Aksesuar silinirken bir hata oluştu.');
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  if (error || !accessory) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          <p>{error || 'Aksesuar bulunamadı.'}</p>
          <Link to="/accessories" className="text-blue-500 hover:underline mt-2 inline-block">
            Aksesuarlara Dön
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(accessory.createdAt).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/accessories" className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Aksesuarlara Dön</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 mt-1">{accessory.name}</h1>
        </div>
        <div className="flex space-x-3">
          <Link 
            to={`/accessories/${id}/edit`} 
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center transition duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Düzenle
          </Link>
          <button 
            onClick={handleDelete} 
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md flex items-center transition duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Sil
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resim Galerisi */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4">
            <h2 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-700" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              Aksesuar Görselleri
            </h2>
            
            {accessory.images && accessory.images.length > 0 ? (
              <div className="space-y-3">
                <div className="h-64 overflow-hidden">
                  <img 
                    src={API_URL+accessory.images[activeImageIndex]} 
                    alt={accessory.name} 
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/placeholder-accessory.png';
                    }}
                  />
                </div>
                
                <div className="flex overflow-x-auto pb-2 space-x-2">
                  {accessory.images.map((image, index) => (
                    <div 
                      key={index} 
                      onClick={() => setActiveImageIndex(index)}
                      className={`h-16 w-16 rounded cursor-pointer flex-shrink-0 overflow-hidden border-2 transition-all ${activeImageIndex === index ? 'border-blue-500' : 'border-gray-200'}`}
                    >
                      <img 
                        src={API_URL+image} 
                        alt={`${accessory.name} - ${index + 1}`} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/placeholder-accessory.png';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center bg-gray-100 rounded-md">
                <span className="text-gray-500">Görsel bulunamadı</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Aksesuar Detayları */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4">
            <h2 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-700" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Aksesuar Bilgileri
            </h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Aksesuar Tipi</div>
                  <div className="font-medium text-lg">{accessory.type}</div>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Renk</div>
                  <div className="flex items-center space-x-2">
                    <div 
                      className="h-6 w-6 rounded-full border border-gray-300" 
                      style={{ backgroundColor: accessory.color }}
                    ></div>
                    <span>{accessory.color}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Eklenme Tarihi</div>
                <div className="font-medium">{formattedDate}</div>
              </div>
              
              {accessory.description && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Açıklama</div>
                  <div className="text-gray-700 whitespace-pre-line">{accessory.description}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessoryDetail;
